import { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Matches the MUI Alert severity="info" palette so the panel looks like
// the info banners used elsewhere in the admin (e.g. Tutor payouts
// "Workflow:" callout). Client asked for this specific look.
const INFO_BG = '#E5F6FD';
const INFO_TEXT = '#014361';
const INFO_ICON = '#0288D1';

/**
 * Collapsible "How this works" panel used on top of admin pages that need
 * a short explanation. Collapsed by default; click the header to expand.
 *
 * Props:
 *   title       — header text (default "How this works")
 *   defaultOpen — start expanded (default false)
 *   maxWidth    — width cap in the same units as sx.maxWidth
 *   children    — the body content (any node — usually a Typography with
 *                 the numbered steps)
 */
const HowItWorksCard = ({
    title = 'How this works',
    defaultOpen = false,
    maxWidth = 760,
    children,
}) => {
    const [open, setOpen] = useState(!!defaultOpen);
    return (
        <Box
            sx={{
                mt: 1.5,
                mb: 2,
                maxWidth,
                borderRadius: 1,
                bgcolor: INFO_BG,
                color: INFO_TEXT,
                '& .MuiTypography-root': { color: INFO_TEXT },
                '& em': { color: INFO_TEXT },
                '& strong': { color: INFO_TEXT },
            }}
        >
            <ButtonBase
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1.25,
                    borderRadius: 1,
                    textAlign: 'left',
                    '&:hover': { bgcolor: 'rgba(2, 136, 209, 0.08)' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlinedIcon sx={{ color: INFO_ICON, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                </Box>
                <ExpandMoreRoundedIcon
                    sx={{
                        color: INFO_ICON,
                        transition: 'transform 200ms',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                />
            </ButtonBase>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pt: 0, pb: 2 }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
};

export default HowItWorksCard;
